import { useTranslation } from "react-i18next";
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, PlayCircleIcon, ClockIcon, TagIcon } from 'lucide-react';
interface TutorialCardProps {
  id: number;
  title: string;
  description: string;
  category: string;
  duration: string;
  completed: boolean;
  completedAt: string | null;
  icon: React.ReactNode;
}
export function TutorialCard({
  id,
  title,
  description,
  category,
  duration,
  completed,
  completedAt,
  icon
}: TutorialCardProps) {
  const {
    t
  } = useTranslation("tutorials");
  const navigate = useNavigate();
  // Format the completion date if available
  const formatCompletionDate = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };
  const completionDate = formatCompletionDate(completedAt);
  const handleCardClick = () => {
    navigate(`/tutorial/${id}`);
  };
  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when button is clicked
    navigate(`/tutorial/${id}`);
  };
  return <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm flex flex-col h-full cursor-pointer" onClick={handleCardClick} data-id="element-13347">
      
      <div className="p-5 flex-grow" data-id="element-13348">
        <div className="flex justify-between items-start mb-3" data-id="element-13349">
          <div className="p-2 rounded-lg bg-gray-50" data-id="element-13350">{icon}</div>
          <div className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${completed ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-gray-100 text-gray-600 border border-gray-200'}`} data-id="element-13351">
            
            {completed ? 'Completed' : 'Not completed'}
          </div>
        </div>
        <h3 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-13352">{title}</h3>
        <p className="text-sm text-gray-500 mb-4 line-clamp-2" data-id="element-13353">{description}</p>
        <div className="flex items-center justify-between mt-auto" data-id="element-13354">
          <div className="flex items-center text-xs text-gray-500" data-id="element-13355">
            <TagIcon size={14} className="mr-1" data-id="element-13356" />
            {category}
          </div>
          <div className="flex items-center text-xs text-gray-500" data-id="element-13357">
            <ClockIcon size={14} className="mr-1" data-id="element-13358" />
            {duration}
          </div>
        </div>
      </div>
      <div className={`px-5 py-3 border-t border-gray-100 flex justify-between items-center ${completed ? 'bg-green-50/50' : 'bg-gray-50/50'}`} data-id="element-13359">
        
        <button onClick={handleButtonClick} className={`flex items-center text-sm font-medium transition-colors ${completed ? 'text-gray-600 hover:text-gray-700' : 'text-blue-600 hover:text-blue-700'}`} data-id="element-13360">
          
          {completed ? <>
              <CheckCircleIcon size={16} className="mr-1.5" data-id="element-13361" />
              {t("components.TutorialCard.revisit_tutorial")}
            </> : <>
              <PlayCircleIcon size={16} className="mr-1.5" data-id="element-13362" />
              {t("components.TutorialCard.start_tutorial")}
            </>}
        </button>
        {completed && completionDate && <span className="text-xs text-gray-500" data-id="element-13363">
            {t("components.TutorialCard.completed")} {completionDate}
          </span>}
      </div>
    </div>;
}